// Device Fingerprint de CyberSource (online-metrix). El script perfila el
// dispositivo en el checkout y el backend correlaciona ese perfil vía
// `device_fingerprint_id`. Las dos mitades son inseparables: un id sin script
// previo llega a CyberSource sin perfil asociado.
//
// Solo Guatemala: el merchant (`visanetgt_propitech`) es el de Visanet GT.
// Plan: PLAN-device-fingerprint.md

const FP_HOST = 'https://h.online-metrix.net/fp'

// El doc exige que el identificador sea único durante 48h.
const TTL_MS = 48 * 60 * 60 * 1000

const STORAGE_PREFIX = 'propi.dfp.'

const ENABLED_COUNTRY = 'gt'

// CRA las inyecta en build time: si faltan al compilar, el feature se apaga solo.
const orgId = (): string => process.env.REACT_APP_METRIX_ORG_ID ?? ''
const merchantId = (): string => process.env.REACT_APP_METRIX_MERCHANT_ID ?? ''

// Latch: el gate se evalúa en cada render, el aviso sale una sola vez.
let missingEnvWarned = false

const warnMissingEnv = (): void => {
  if (missingEnvWarned) return
  missingEnvWarned = true

  const missing: string[] = []
  // org_id: sin esto no se puede construir la URL de online-metrix.
  if (!orgId()) missing.push('REACT_APP_METRIX_ORG_ID')
  // merchant id: es el prefijo del session_id que espera CyberSource.
  if (!merchantId()) missing.push('REACT_APP_METRIX_MERCHANT_ID')

  const falta = missing.length === 1 ? 'Falta la variable' : 'Faltan las variables'
  console.error(
    `[DeviceFingerprint] ${falta} ${missing.join(' y ')} en el build. ` +
      'El fingerprint NO se carga ni se envía. Revisar las env ' +
      'vars del deploy (CRA las inyecta en build time, no en runtime).'
  )
}

/**
 * Gate ÚNICO del feature. Todo cuelga de acá para que las dos mitades (script y
 * campo en el POST) no puedan desincronizarse.
 *
 * Solo avisa por env faltante cuando el país es GT: en SV/PA no falta nada, el
 * feature simplemente no aplica.
 */
export const isDeviceFingerprintEnabled = (country?: string | null): boolean => {
  if (String(country ?? '').toLowerCase() !== ENABLED_COUNTRY) return false
  if (!orgId() || !merchantId()) {
    warnMissingEnv()
    return false
  }
  return true
}

// `getRandomValues` y no `randomUUID`: el segundo exige secure context.
const randomHex = (bytes: number): string => {
  const webCrypto = typeof window !== 'undefined' ? window.crypto : undefined
  if (webCrypto?.getRandomValues) {
    const buf = new Uint8Array(bytes)
    webCrypto.getRandomValues(buf)
    return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('')
  }
  let out = ''
  while (out.length < bytes * 2) out += Math.floor(Math.random() * 16).toString(16)
  return out.slice(0, bytes * 2)
}

// Timestamp base36 (monotónico) + 12 hex de entropía. Sin mayúsculas: el doc
// advierte no usar las mismas letras en distinta capitalización.
const newIdentifier = (): string => `${Date.now().toString(36)}-${randomHex(6)}`

interface StoredFingerprint {
  id: string
  createdAt: number
}

// `sessionStorage` tira en modo privado y esto NO puede romper un checkout.
// Si falla, el id vive en memoria: se pierde al recargar, pero el flujo sigue.
const memoryFallback = new Map<string, StoredFingerprint>()

const readStored = (key: string): StoredFingerprint | null => {
  try {
    const raw = window.sessionStorage.getItem(key)
    if (raw) return JSON.parse(raw) as StoredFingerprint
  } catch {
    // storage bloqueado → fallback en memoria
  }
  return memoryFallback.get(key) ?? null
}

const writeStored = (key: string, value: StoredFingerprint): void => {
  memoryFallback.set(key, value)
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value))
  } catch {
    // storage bloqueado → ya quedó en memoria
  }
}

/**
 * Identificador único de la operación, o `undefined` si el feature está apagado.
 *
 * Idempotente por `scope`: el mismo checkout devuelve el mismo id dentro de las
 * 48h. Es deliberado — el profiling queda atado al `session_id` con el que se
 * cargó el script, así que recargar o reintentar debe conservarlo.
 *
 * @param scope la operación: `transaction_id`, o `transaction_id:detail_id`.
 */
export const getDeviceFingerprintId = (
  scope: string,
  country?: string | null
): string | undefined => {
  if (!isDeviceFingerprintEnabled(country) || !scope) return undefined

  const key = `${STORAGE_PREFIX}${scope}`
  const stored = readStored(key)
  if (stored?.id && Date.now() - stored.createdAt < TTL_MS) return stored.id

  const fresh: StoredFingerprint = { id: newIdentifier(), createdAt: Date.now() }
  writeStored(key, fresh)
  return fresh.id
}

/**
 * `session_id` para online-metrix: merchantID + identificador, sin separador.
 * Al backend se manda SOLO el identificador, sin el prefijo del merchant.
 */
export const buildSessionId = (deviceFingerprintId: string): string =>
  `${merchantId()}${deviceFingerprintId}`

export const fingerprintScriptUrl = (sessionId: string): string =>
  `${FP_HOST}/tags.js?org_id=${orgId()}&session_id=${sessionId}`

export const fingerprintIframeUrl = (sessionId: string): string =>
  `${FP_HOST}/tags?org_id=${orgId()}&session_id=${sessionId}`
